/**
 * Department learning-loop suggestions (D4, workspace docs/guardian/10, rev 3.1,
 * 2026-10-24). Admin only. The Backend computes these on read from the last 14
 * days of member signals and groups them into unknown terms, questions with no
 * rule, corrections and down-rated rules. Answering or drafting creates a DRAFT
 * term (approved in the vocabulary section); dismiss hides the suggestion.
 *
 * The API carries NO member identity — this view never shows who asked, and
 * adds no identity of its own. All member text is rendered as text only. A 503
 * `signals_unavailable` hides the whole section.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  answerDepartmentSuggestion,
  dismissDepartmentSuggestion,
  draftDepartmentSuggestion,
  fetchDepartmentSuggestions,
} from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';

function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const act = async (fn) => {
    setBusy(true);
    setError(null);
    const err = await fn();
    setBusy(false);
    if (err) setError(err);
  };
  return { busy, error, act };
}

function TokenCard({ item, windowDays, onAnswer, onDismiss }) {
  const [meaning, setMeaning] = useState('');
  const { busy, error, act } = useAction();
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="suggestion-token">
      <Typography variant="body1">
        {`${item.token} was used by ${item.members} members in the last ${windowDays} days. What does it mean?`}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        <TextField
          size="small"
          label="What it means"
          value={meaning}
          onChange={(e) => setMeaning(e.target.value)}
          inputProps={{ maxLength: 300 }}
          sx={{ flex: '1 1 240px' }}
        />
      </Box>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          variant="contained"
          size="small"
          disabled={busy || !meaning.trim()}
          onClick={() => act(() => onAnswer(item, { meaning: meaning.trim() }))}
        >
          Save
        </Button>
        <Button size="small" disabled={busy} onClick={() => act(() => onDismiss(item))}>
          Not used here
        </Button>
      </Box>
    </Paper>
  );
}

function TopicCard({ item, examplesShown, onAddRule, onDismiss }) {
  const { busy, error, act } = useAction();
  const examples = examplesShown ? (item.examples || []) : [];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="suggestion-topic">
      <Typography variant="body1">
        {`${(item.keywords || []).join(', ')} — asked by ${item.members} members`}
      </Typography>
      {examples.map((ex) => (
        <Typography
          key={ex}
          variant="caption"
          color="text.secondary"
          component="blockquote"
          sx={{ display: 'block', mt: 0.5 }}
        >
          {ex}
        </Typography>
      ))}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        {onAddRule && (
          <Button variant="contained" size="small" disabled={busy} onClick={() => onAddRule(item)}>
            Add a rule
          </Button>
        )}
        <Button size="small" disabled={busy} onClick={() => act(() => onDismiss(item))}>
          Dismiss
        </Button>
      </Box>
    </Paper>
  );
}

function CorrectionCard({ item, onDraft, onDismiss }) {
  const { busy, error, act } = useAction();
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="suggestion-correction">
      <Typography variant="body1">
        {`A member said ${item.surface} means ${item.meaning}.`}
      </Typography>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button variant="contained" size="small" disabled={busy} onClick={() => act(() => onDraft(item))}>
          Save as draft
        </Button>
        <Button size="small" disabled={busy} onClick={() => act(() => onDismiss(item))}>
          Dismiss
        </Button>
      </Box>
    </Paper>
  );
}

function RuleCard({ item, onDismiss }) {
  const { busy, error, act } = useAction();
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="suggestion-rule">
      <Typography variant="body1">
        {`Rule ${item.ruleKey} was cited in ${item.downRated} answers rated not helpful.`}
      </Typography>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button size="small" disabled={busy} onClick={() => act(() => onDismiss(item))}>
          Dismiss
        </Button>
      </Box>
    </Paper>
  );
}

function Group({ title, items, children }) {
  if (!items || items.length === 0) return null;
  return (
    <Box>
      <Typography variant="subtitle1" component="h3" sx={{ mt: 2 }}>{title}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>{children}</Box>
    </Box>
  );
}

/**
 * @param {object} props
 * @param {string|null} props.authToken
 * @param {(topic: object) => void} [props.onAddRule] - Optional; "Add a rule"
 *   is hidden when not provided.
 */
export default function DepartmentSuggestions({ authToken, onAddRule }) {
  const [data, setData] = useState(null);
  const [off, setOff] = useState(false);
  const [error, setError] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    const res = await fetchDepartmentSuggestions(authToken);
    if (!alive.current) return;
    if (res?.success) { setData(res.data); setError(null); }
    else if (res?.status === 503) setOff(true);
    else setError(departmentErrorMessage(res, 'Could not load your department suggestions.'));
  }, [authToken]);
  useEffect(() => { load(); }, [load]);

  // Returns an error string for the card to show, or null; reloads on success
  // so the acted-on suggestion drops out of the recomputed set.
  const after = async (res) => {
    if (!res?.success) return departmentErrorMessage(res);
    await load();
    return null;
  };
  const onAnswer = async (item, body) => after(await answerDepartmentSuggestion(authToken, item.key, body));
  const onDraft = async (item) => after(await draftDepartmentSuggestion(authToken, item.key));
  const onDismiss = async (item) => after(await dismissDepartmentSuggestion(authToken, item.key));

  if (off) return null;
  if (!data) return error ? <Alert severity="error">{error}</Alert> : null;

  const tokens = data.tokens || [];
  const topics = data.topics || [];
  const corrections = data.corrections || [];
  const rules = data.rules || [];
  const windowDays = data.windowDays ?? 14;
  const empty = tokens.length === 0 && topics.length === 0 && corrections.length === 0 && rules.length === 0;

  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-labelledby="dept-suggestions-title">
      <Typography id="dept-suggestions-title" variant="h6" component="h2">Suggestions</Typography>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {empty && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No suggestions right now.</Typography>
      )}

      <Group title="Unknown terms" items={tokens}>
        {tokens.map((it) => (
          <TokenCard key={it.key} item={it} windowDays={windowDays} onAnswer={onAnswer} onDismiss={onDismiss} />
        ))}
      </Group>

      <Group title="Questions with no rule" items={topics}>
        {topics.map((it) => (
          <TopicCard key={it.key} item={it} examplesShown={!!data.examplesShown} onAddRule={onAddRule} onDismiss={onDismiss} />
        ))}
      </Group>

      <Group title="Corrections" items={corrections}>
        {corrections.map((it) => (
          <CorrectionCard key={it.key} item={it} onDraft={onDraft} onDismiss={onDismiss} />
        ))}
      </Group>

      <Group title="Rules rated down" items={rules}>
        {rules.map((it) => (
          <RuleCard key={it.key} item={it} onDismiss={onDismiss} />
        ))}
      </Group>
    </Paper>
  );
}
